import { useState, useCallback } from 'react';

interface FileErrorDetailsProps {
  fileId: string;
  fileName: string;
  status: string;
  errorStage?: string | null;
  errorMessage?: string | null;
}

/**
 * Rendered inside ActionModal when a user clicks "View error" on a failed
 * file row. Shows the stored error details and offers a single Copy button
 * so the user can paste the report into a support message.
 *
 * Designed to be rendered via:
 *   dispatchAction({
 *     type: START_ACTION,
 *     payload: {
 *       name: 'File processing error',
 *       component: <FileErrorDetails {...} />,
 *       noContinue: true,  // hides the submit button in ActionModal's footer
 *     },
 *   });
 */
export default function FileErrorDetails({
  fileId,
  fileName,
  status,
  errorStage,
  errorMessage,
}: FileErrorDetailsProps) {
  const [copied, setCopied] = useState(false);

  // The full report the user copies into a support message. Includes the
  // file id and status so support can find the record on their side without
  // a back-and-forth.
  const report = [
    `File: ${fileName}`,
    `File ID: ${fileId}`,
    `Status: ${status}`,
    errorStage ? `Stage: ${errorStage}` : null,
    '',
    '--- Error details ---',
    errorMessage || '(No error message was captured.)',
  ]
    .filter((line) => line !== null)
    .join('\n');

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(report);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      // Older browsers / non-secure contexts may not have the API.
      // Fall back to a hidden textarea selection.
      try {
        const ta = document.createElement('textarea');
        ta.value = report;
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch {
        // Last-resort: let the user select manually.
        setCopied(false);
      }
    }
  }, [report]);

  return (
    <div className="flex flex-col gap-4">
      <p className="text-sm text-gray-700">
        We hit an error while processing your file. Please copy the details
        below and forward them to support so we can investigate.
      </p>

      <div className="rounded border border-gray-200 bg-gray-50 p-3 text-sm">
        <div className="grid grid-cols-[120px_1fr] gap-y-1">
          <span className="font-semibold text-gray-700">File:</span>
          <span className="text-gray-900 break-all">{fileName}</span>

          <span className="font-semibold text-gray-700">File ID:</span>
          <span className="font-mono text-gray-900 break-all">{fileId}</span>

          <span className="font-semibold text-gray-700">Status:</span>
          <span className="text-gray-900 capitalize">
            {status.replace('-', ' ')}
          </span>

          {errorStage && (
            <>
              <span className="font-semibold text-gray-700">Stage:</span>
              <span className="text-gray-900">{errorStage}</span>
            </>
          )}
        </div>
      </div>

      <div>
        <p className="text-sm font-semibold text-gray-700 mb-2">
          Error message
        </p>
        <pre
          className="bg-gray-900 text-gray-100 text-xs font-mono p-3 rounded max-h-80 overflow-auto whitespace-pre-wrap break-words"
          aria-label="File error message"
        >
          {errorMessage || '(No error message was captured for this file.)'}
        </pre>
      </div>

      <div className="flex items-center gap-3 pt-2">
        <button
          type="button"
          onClick={handleCopy}
          className="bg-[#14b8a6] text-white font-medium px-4 py-2 rounded-md hover:bg-teal-600 transition-colors text-sm"
        >
          {copied ? 'Copied!' : 'Copy error to clipboard'}
        </button>
        {copied && (
          <span className="text-sm text-green-700">
            Paste it into a message to support.
          </span>
        )}
      </div>
    </div>
  );
}
